import React, { useState, useEffect } from "react";
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Typography,
    Divider,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert
} from "@mui/material";
import {
    ExpandLess,
    ExpandMore,
    Palette,
    Save,
    Refresh
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * Componente SidebarMenu unificado y reutilizable
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.sections - Array de secciones del menú
 * @param {Object} props.colors - Configuración de colores personalizados
 * @param {string} props.title - Título del menú
 * @param {React.ReactNode} props.logo - Logo del menú
 * @param {boolean} props.showColorPicker - Mostrar selector de colores (solo para admins)
 * @param {Function} props.onColorsChange - Callback cuando cambian los colores
 * @param {Object} props.defaultColors - Colores por defecto
 */
const SidebarMenu = ({
    sections = [],
    colors: customColors,
    title = "CAPIVIO Admin",
    logo,
    showColorPicker = false,
    onColorsChange,
    defaultColors = {
        background: "#1a237e",
        text: "#ffffff",
        activeItem: "#3949ab",
        hoverItem: "#283593",
        divider: "#e0e0e0"
    }
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin } = useAuth();
    
    // Estado para expansión de secciones
    const [expandedSections, setExpandedSections] = useState({});
    
    // Estado para personalización de colores
    const [colorDialogOpen, setColorDialogOpen] = useState(false);
    const [tempColors, setTempColors] = useState(defaultColors);
    
    // Colores activos (personalizados o por defecto)
    const activeColors = customColors || defaultColors;

    // Inicializar secciones expandidas basado en la ruta actual
    useEffect(() => {
        const newExpandedSections = {};
        sections.forEach(section => {
            if (section.items) {
                const hasActiveItem = section.items.some(item => 
                    item.path && location.pathname.startsWith(item.path)
                );
                if (hasActiveItem) {
                    newExpandedSections[section.id] = true;
                }
            }
        });
        setExpandedSections(newExpandedSections);
    }, [location.pathname, sections]);

    // Inicializar colores desde localStorage si es admin
    useEffect(() => {
        if (isAdmin && showColorPicker) {
            const savedColors = localStorage.getItem("sidebarColors");
            if (savedColors) {
                try {
                    const parsedColors = JSON.parse(savedColors);
                    setTempColors({ ...defaultColors, ...parsedColors });
                    // NO LLAMAR onColorsChange aquí para evitar loops
                } catch (error) {
                    // Error parsing saved colors
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Solo al montar

    const handleSectionToggle = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const handleItemClick = (item) => {
        if (item.path) {
            navigate(item.path);
        }
        if (item.onClick) {
            item.onClick();
        }
    };

    const handleColorChange = (colorKey, value) => {
        setTempColors(prev => ({
            ...prev,
            [colorKey]: value
        }));
    };

    const handleSaveColors = () => {
        if (isAdmin) {
            localStorage.setItem("sidebarColors", JSON.stringify(tempColors));
            if (onColorsChange) {
                onColorsChange(tempColors);
            }
        }
        setColorDialogOpen(false);
    };

    const handleResetColors = () => {
        setTempColors(defaultColors);
        if (isAdmin) {
            localStorage.removeItem("sidebarColors");
            if (onColorsChange) {
                onColorsChange(defaultColors);
            }
        }
    };

    const isItemActive = (item) => {
        if (!item.path) return false;
        return location.pathname === item.path || 
               (item.path !== "/" && location.pathname.startsWith(item.path));
    };

    const isSectionActive = (section) => {
        if (!section.items) return false;
        return section.items.some(item => isItemActive(item));
    };

    return (
        <Box
            sx={{
                height: "100%",
                backgroundColor: activeColors.background,
                color: activeColors.text,
                display: "flex",
                flexDirection: "column"
            }}
        >
            {/* Header del menú */}
            <Box sx={{ p: 2, borderBottom: `1px solid ${activeColors.divider}` }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {logo && <Box sx={{ mr: 1 }}>{logo}</Box>}
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {title}
                        </Typography>
                    </Box>
                    
                    {/* Botón de personalización de colores (solo para admins) */}
                    {isAdmin && showColorPicker && (
                        <Tooltip title="Personalizar colores">
                            <IconButton
                                size="small"
                                onClick={() => setColorDialogOpen(true)}
                                sx={{ color: activeColors.text }}
                            >
                                <Palette />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Contenido del menú */}
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                <List component="nav" sx={{ py: 0 }}>
                    {sections.map((section, sectionIndex) => (
                        <Box key={section.id || `section-${sectionIndex}`}>
                            {/* Sección con subitems */}
                            {section.items ? (
                                <>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => handleSectionToggle(section.id)}
                                            sx={{
                                                backgroundColor: isSectionActive(section) 
                                                    ? activeColors.activeItem 
                                                    : "transparent",
                                                "&:hover": {
                                                    backgroundColor: activeColors.hoverItem
                                                }
                                            }}
                                        >
                                            {section.icon && (
                                                <ListItemIcon sx={{ color: activeColors.text, minWidth: 40 }}>
                                                    {section.icon}
                                                </ListItemIcon>
                                            )}
                                            <ListItemText 
                                                primary={section.title}
                                                sx={{ 
                                                    "& .MuiListItemText-primary": {
                                                        color: activeColors.text,
                                                        fontWeight: isSectionActive(section) ? "bold" : "normal"
                                                    }
                                                }}
                                            />
                                            {expandedSections[section.id] ? <ExpandLess /> : <ExpandMore />}
                                        </ListItemButton>
                                    </ListItem>
                                    
                                    <Collapse in={expandedSections[section.id]} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            {section.items.map((item, index) => (
                                                <ListItem key={item.id || `item-${section.id}-${index}`} disablePadding>
                                                    <ListItemButton
                                                        sx={{
                                                            pl: 4,
                                                            backgroundColor: isItemActive(item) 
                                                                ? activeColors.activeItem 
                                                                : "transparent",
                                                            "&:hover": {
                                                                backgroundColor: activeColors.hoverItem
                                                            }
                                                        }}
                                                        onClick={() => handleItemClick(item)}
                                                    >
                                                        {item.icon && (
                                                            <ListItemIcon sx={{ color: activeColors.text, minWidth: 40 }}>
                                                                {item.icon}
                                                            </ListItemIcon>
                                                        )}
                                                        <ListItemText 
                                                            primary={item.title}
                                                            sx={{ 
                                                                "& .MuiListItemText-primary": {
                                                                    color: activeColors.text,
                                                                    fontWeight: isItemActive(item) ? "bold" : "normal"
                                                                }
                                                            }}
                                                        />
                                                        {item.badge && (
                                                            <Chip 
                                                                label={item.badge} 
                                                                size="small" 
                                                                color="error"
                                                                sx={{ ml: 1 }}
                                                            />
                                                        )}
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Collapse>
                                </>
                            ) : (
                                /* Sección sin subitems (enlace directo) */
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => handleItemClick(section)}
                                        sx={{
                                            backgroundColor: isItemActive(section) 
                                                ? activeColors.activeItem 
                                                : "transparent",
                                            "&:hover": {
                                                backgroundColor: activeColors.hoverItem
                                            }
                                        }}
                                    >
                                        {section.icon && (
                                            <ListItemIcon sx={{ color: activeColors.text, minWidth: 40 }}>
                                                {section.icon}
                                            </ListItemIcon>
                                        )}
                                        <ListItemText 
                                            primary={section.title}
                                            sx={{ 
                                                "& .MuiListItemText-primary": {
                                                    color: activeColors.text,
                                                    fontWeight: isItemActive(section) ? "bold" : "normal"
                                                }
                                            }}
                                        />
                                        {section.badge && (
                                            <Chip 
                                                label={section.badge} 
                                                size="small" 
                                                color="error"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            )}
                            
                            {section.divider && (
                                <Divider sx={{ backgroundColor: activeColors.divider, my: 1 }} />
                            )}
                        </Box>
                    ))}
                </List>
            </Box>

            {/* Footer con información adicional si es necesario */}
            {sections.some(s => s.footer) && (
                <Box sx={{ p: 2, borderTop: `1px solid ${activeColors.divider}` }}>
                    {sections.find(s => s.footer)?.footer}
                </Box>
            )}

            {/* Dialog de personalización de colores */}
            <Dialog 
                open={colorDialogOpen} 
                onClose={() => setColorDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Personalizar Colores del Menú
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Los cambios se guardarán automáticamente y se aplicarán a todos los menús del sistema.
                    </Alert>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Color de Fondo"
                                type="color"
                                value={tempColors.background}
                                onChange={(e) => handleColorChange("background", e.target.value)}
                                InputProps={{
                                    style: { height: 56 }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Color de Texto"
                                type="color"
                                value={tempColors.text}
                                onChange={(e) => handleColorChange("text", e.target.value)}
                                InputProps={{
                                    style: { height: 56 }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Item Activo"
                                type="color"
                                value={tempColors.activeItem}
                                onChange={(e) => handleColorChange("activeItem", e.target.value)}
                                InputProps={{
                                    style: { height: 56 }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Item Hover"
                                type="color"
                                value={tempColors.hoverItem}
                                onChange={(e) => handleColorChange("hoverItem", e.target.value)}
                                InputProps={{
                                    style: { height: 56 }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Color de Divisores"
                                type="color"
                                value={tempColors.divider}
                                onChange={(e) => handleColorChange("divider", e.target.value)}
                                InputProps={{
                                    style: { height: 56 }
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button 
                        startIcon={<Refresh />}
                        onClick={handleResetColors}
                    >
                        Restaurar
                    </Button>
                    <Button 
                        startIcon={<Save />}
                        onClick={handleSaveColors}
                        variant="contained"
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SidebarMenu; 